<template>
  <div
    class="selection-tab"
    :class="{
      'selection-tab--button': url,
      [$class('disabled')]: disabled || visuallyDisabled,
      [$class('loading')]: loading,
    }"
  >
    <a
      v-if="url"
      class="selection-tab__label text-body-s-regular text-body-m-regular-desktop"
      :href="url"
      :title="title"
      v-html="label"
    />
    <template v-else>
      <input
        :id="id"
        v-model="localModelValue"
        class="selection-tab__input"
        :class="{
          [$class('disabled')]: disabled || visuallyDisabled,
          [$class('loading')]: loading,
        }"
        :disabled="disabled || loading"
        :name="name"
        type="radio"
        :value="value"
        @change="updateVModel(value)"
      />

      <label
        class="selection-tab__label text-body-s-regular text-body-m-regular-desktop"
        :for="id"
        :title="title"
      >
        <span v-if="showUpsizing" class="selection-tab__discount"> % </span>

        <span
          v-if="
            (loading && featuredImage) || Object.keys(featuredImage)?.length
          "
          class="selection-tab__image-container"
        >
          <responsive-image
            class="selection-tab__image"
            :image="featuredImage"
            :padding="false"
            ratio="1:1"
            sizes="75px"
            widths="75, 150, 225"
          />
        </span>

        <p
          class="text-mobile-paragraph-m-semibold text-desktop-paragraph-m-semibold"
          v-text="label"
        />

        <p
          v-if="portions"
          class="text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
          v-text="portionText"
        />

        <p
          v-if="showInfoText"
          class="text-mobile-paragraph-2xs-regular text-desktop-paragraph-2xs-regular"
          v-text="infoText"
        />
      </label>
      <div
        v-if="showUpsizing"
        class="selection-tab__notification text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
      >
        <div
          ref="tooltipInner"
          class="selection-tab__notification-inner"
          :style="{ right: tooltipRight }"
        >
          <icon-info />
          <span v-text="$string('product.best_price')" />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
/**
 * Vue: Selection tab (selection-tab)
 * -----------------------------------------------------------------------------
 * Simple tab button with active, disabled, and initial states.
 *
 * @param {Boolean} [disabled] - Tab is disabled.
 * @param {Object|Boolean} [featuredImage] - The variant image source.
 * @param {String} [id] - ID for input, required for input.
 * @param {String} label - Tab label.
 * @param {Boolean} [loading] - Loading state.
 * @param {Number|String} modelValue - Current value provided by v-model. This
 * is required when using `input` element (when no `url` prop is passed).
 * @param {String} [name] - Radio input group name, required for input.
 * @param {String} [title] - Title attribute.
 * @param {String} [url] - URL to navigate to when clicked, replaces input.
 * @param {Number|String} [value] - Radio input value, required for input.
 * @param {Boolean} [visuallyDisabled] - Tab is visually disabled but can still
 * be selected.
 *
 * @emits update:model-value - Emit v-model update event.
 *
 */

import { mapState } from 'pinia';
import useIndexStore from '~stores/index';

import ResponsiveImage from '~global/images/responsive-image';
import IconInfo from '~icons/general/info.svg';

export default {
  name: 'SelectionTab',

  components: {
    IconInfo,
    ResponsiveImage,
  },

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
    featuredImage: {
      type: [Boolean, Object],
      default: () => ({}),
    },
    id: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showUpsizing: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: [Number, String],
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    portions: {
      type: [Number, String],
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    url: {
      type: String,
      default: '',
    },
    value: {
      type: [Number, String],
      default: '',
    },
    visuallyDisabled: {
      type: Boolean,
      default: false,
    },
    showBackInStockEmailForm: {
      type: Boolean,
      default: true,
    },
  },

  emits: {
    'update:model-value': () => true,
  },

  data() {
    return {
      localModelValue: '',
      tooltipRight: 0,
    };
  },

  computed: {
    /**
     * Map Pinia state.
     */
    ...mapState(useIndexStore, {
      screen: 'screen',
    }),

    /**
     * Compute portion text.
     * @returns {String}
     */
    portionText() {
      const options = {
        pluralise: Number(this.portions),
        replace: { count: this.portions },
      };

      return this.$string('product.variant.portion_count', options);
    },

    showInfoText() {
      return this.disabled || this.visuallyDisabled;
    },

    infoText() {
      if (this.showInfoText && this.showBackInStockEmailForm) {
        return this.$string('product.variant.notify_me');
      }
      return this.$string('product.cta.sold_out');
    },
  },

  watch: {
    /**
     * Watch v-model value to update local value.
     */
    modelValue() {
      this.localModelValue = this.modelValue;
    },

    variant() {
      if (!this.variant) return;

      if (this.showUpsizing) {
        this.moveNotification(this.screen);
      }
    },
  },

  mounted() {
    this.localModelValue = this.modelValue;

    if (this.showUpsizing) {
      this.moveNotification(this.screen);
    }

    cnvs.EventBus.listen('cnvs:resize', (screen) => {
      this.moveNotification(screen);
    });
  },

  methods: {
    /**
     * Emit update v-model event.
     * @param {Number|String} value - Update value.
     */
    updateVModel(value) {
      this.$emit('update:model-value', value);
    },

    async moveNotification(screen) {
      await this.$nextTick();
      if (!this.$refs.tooltipInner) return;

      const boundingRect = this.$refs.tooltipInner.getBoundingClientRect();
      const offsetRight = this.$refs.tooltipInner.offsetLeft * -1;
      const mainProduct = screen.mobile
        ? document.querySelector('.main-product__main')
        : document.querySelector('.main-product');
      let padding;

      // get padding by reading main-product container
      if (mainProduct) {
        // padding could be 0, which would be legit
        padding = parseInt(
          getComputedStyle(mainProduct)
            .getPropertyValue('padding-right')
            .replace('px', ''),
        );
      }

      // padding fallback 1 - use CSS variable
      if (isNaN(padding) || padding === 'undefined') {
        const layoutMargin = getComputedStyle(
          document.documentElement,
        ).getPropertyValue('--layout-margin');

        // convert to px
        if (layoutMargin.includes('em')) {
          padding =
            parseFloat(layoutMargin.replace(/em|rem/, '')) *
            parseFloat(getComputedStyle(document.documentElement).fontSize);
        } else {
          padding = parseInt(layoutMargin.replace('px', ''));
        }

        // fallback 2
        if (isNaN(padding)) {
          padding = 24;
        }
      }

      // remove padding if total tooltip width exceeds screen width
      if (boundingRect.width + padding > screen.width) {
        padding = 0;
      }

      // move tooltip, include padding
      if (boundingRect.right > screen.width) {
        // move away from right screen border
        const right = offsetRight + boundingRect.right - screen.width + padding;
        this.tooltipRight = `${right}px`;
      } else if (boundingRect.left < 0) {
        // move away from left screen border
        const right = offsetRight + boundingRect.left - padding;
        this.tooltipRight = `${right}px`;
      } else {
        // move towards closest screen border
        const deltaRight = screen.width - boundingRect.right;
        let right;

        if (boundingRect.left < deltaRight) {
          // move towards left screen border
          right = boundingRect.left - padding + offsetRight;
        } else {
          // move towards right screen border
          right = offsetRight + padding - (screen.width - boundingRect.right);

          if (boundingRect.left > 0 && right < 0) {
            right = 0;
          }
        }

        this.tooltipRight = `${right}px`;
      }
    },
  },
};
</script>

<style lang="scss">
@import './selection-tab';
</style>
